import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";

import { LanguageProvider } from "@/i18n";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import QueueManagement from "@/pages/QueueManagement";
import LiveQueue from "@/pages/LiveQueue";
import Track from "@/pages/Track";
import PrintSlip from "@/pages/PrintSlip";
import CrateTags from "@/pages/CrateTags";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import Products from "@/pages/Products";
import Storefront from "@/pages/Storefront";
import Analytics from "@/pages/Analytics";
import Billing from "@/pages/Billing";
import Receipt from "@/pages/Receipt";
import OrderLookup from "@/pages/OrderLookup";

function AppRouter() {
  const location = useLocation();
  // Handle Google OAuth callback synchronously before any route/auth check.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/live-queue/:slug" element={<LiveQueue />} />
      <Route path="/track/:orderCode" element={<Track />} />
      <Route path="/store/:slug" element={<Storefront />} />
      <Route path="/order-status" element={<OrderLookup />} />
      <Route path="/order-status/:code" element={<OrderLookup />} />
      <Route path="/print-slip/:orderId" element={<PrintSlip />} />
      <Route path="/crate-tags" element={<CrateTags />} />
      <Route path="/crate-tags/:orderId" element={<CrateTags />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
      <Route path="/queue-management" element={<ProtectedRoute><QueueManagement /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/receipt/:orderId" element={<ProtectedRoute><Receipt /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  useEffect(() => { document.title = "e-olive Albania — Olive Mill Management"; }, []);
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AuthProvider>
              {paypalClientId ? (
                <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "EUR", intent: "capture" }}>
                  <AppRouter />
                </PayPalScriptProvider>
              ) : (
                <AppRouter />
              )}
              <Toaster position="top-right" richColors />
            </AuthProvider>
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
